import React, { useEffect, useMemo, useState } from 'react';
import { useTncDialog } from './desktop/TncDialog';
import { AccountApiResponse, Plans } from '../..';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedOutgoingFormat,
  SupportedServers,
} from '../../../common/ApiHelper';
import { appConfig } from '../../../common/ConfigLoader';
import {
  PlanCardProps,
  Prices,
  basePlans,
  planPrices,
} from '../../../component/parentPortal/planCard/planCard';
import { useAppSelector } from '../../../store/hooks';
import { selectAppAuthentication } from '../../../store/reducer/appStateSlice';

export interface ChoosePaymentMethodElementInterface {
  planProps: PlanCardProps;
  planPrices: Prices;
  fullPayButtonClickHandler: () => void;
  bnplButtonClickHandler: () => void;
  openTnc: () => void;
  tncDialog: JSX.Element;
  continueButtonClickHandler: () => void;
  loading: boolean;
  selectedPaymentMethod?: 'fullPay' | 'bnpl';
  continueDisabled: () => boolean;
  handelTncCheckBoxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  showAddressForm: boolean;
  closeAddressForm: () => void;
  bnpl: (address: any) => void;
}

export const ChoosePaymentMethodElement = (
  selectedPlan: Plans,
): ChoosePaymentMethodElementInterface => {
  // Redux
  const authenticationData = useAppSelector(selectAppAuthentication);
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<ChoosePaymentMethodElementInterface['selectedPaymentMethod']>();
  const [acceptedTnc, setAcceptedTnc] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const { openTnc, tncDialog } = useTncDialog();

  const fullPayButtonClickHandler = () => {
    setSelectedPaymentMethod('fullPay');
  };
  const bnplButtonClickHandler = () => {
    setSelectedPaymentMethod('bnpl');
  };

  const fullPay = async () => {
    const fullPayEndpoint = 'user/UserSubscribe';
    await apiHelper(
      SupportedServers.ACCOUNT,
      fullPayEndpoint,
      SupportedMethod.POST,
      {
        subscriptionName: selectedPlan,
        clientSessionId: authenticationData?.sessionId,
        subscriptionMode: 'NEW',
        signature: 'pass',
      },
      {
        incomingFormat: SupportedIncomingFormat.JSON,
        outgoingFormat: SupportedOutgoingFormat.JSON,
        headerConstructor() {
          return {
            authorization: authenticationData ? authenticationData.token : '',
          };
        },
      },
    ).then(async (res): Promise<void> => {
      const result = res.data as AccountApiResponse;
      console.log(res);
      if (result.success) {
        if (result.url) {
          // Checkout Created Successful
          window.location.href = result.url;
        }
      }
    });
  };

  const bnpl = async (shippingAddress: any) => {
    // Send Request
    await apiHelper(
      SupportedServers.ACCOUNT,
      `/v2/payment/sales`,
      SupportedMethod.POST,
      {
        clientSessionId: authenticationData?.sessionId,
        signature: 'pass',
        subscriptionMode: 'NEW',
        subscriptionName: selectedPlan,
        instalment: 'Y',
        shippingAddress,
      },
      {
        incomingFormat: SupportedIncomingFormat.JSON,
        outgoingFormat: SupportedOutgoingFormat.JSON,
        headerConstructor() {
          return {
            authorization: authenticationData ? authenticationData.token : '',
          };
        },
      },
    )
      .then(async (result) => {
        console.log(result);
        if (!result.success) {
          // @TODO: handle server side error
        } else {
          const formId = 'cityline-form';
          // Check for existing form
          const existingForm = document.getElementById(formId);
          if (existingForm) {
            existingForm.remove();
          }
          const data = result.data.data.formData;
          const form = document.createElement('form');
          form.setAttribute('id', formId);
          form.action = result.data.data.url;
          form.method = 'POST';
          form.style.display = 'none';

          data.signType = 'SHA-256';
          Object.keys(data).forEach((key) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = data[key];
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          form.remove();
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const continueButtonClickHandler = async () => {
    setLoading(true);
    if (selectedPaymentMethod === 'fullPay') {
      await fullPay();
    } else if (selectedPaymentMethod === 'bnpl') {
      setShowAddressForm(true);
    }

    setLoading(false);
  };

  const handelTncCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptedTnc(e.target.checked);
  };

  return {
    planProps: basePlans[selectedPlan],
    planPrices: planPrices[selectedPlan],
    fullPayButtonClickHandler,
    bnplButtonClickHandler,
    openTnc,
    tncDialog,
    continueButtonClickHandler,
    loading,
    selectedPaymentMethod,
    continueDisabled: () => {
      return !(acceptedTnc && selectedPaymentMethod !== undefined && !loading);
    },
    handelTncCheckBoxChange,
    showAddressForm,
    closeAddressForm: () => {
      setShowAddressForm(false);
    },
    bnpl,
  };
};
